/**
 * Every words → milliseconds rate a topic uses (R13). Silent reading, narration speech and the
 * pre-measurement TTS hint all live here; nothing else converts text to time.
 */

/** Adult silent reading speed for micro-learning cards. */
export const TOPIC_READING_WPM = 200;

/** Speech rate at 1× used for narration script targets (not reading pace). */
export const NARRATION_SPEECH_WPM = 150;
export const NARRATION_CARD_MIN_MS = 3_000;
export const NARRATION_CARD_MAX_MS = 600_000;

/**
 * Browser speech fallback: a first-visit length hint until a measured utterance supplies the real
 * one (Web Speech has no pre-play duration API).
 */
export const FALLBACK_TTS_PLANNING_SPEECH_WPM = 125;
export const FALLBACK_TTS_PLANNING_MIN_MS = 5_000;

/** Hold added after reading for interactive kinds (F3). */
export const BLOCK_INTERACTION_HOLD_MS = {
	IMAGE: 5_000,
	VIDEO_UNKNOWN_LENGTH: 15_000,
	QUIZ_PER_QUESTION: 10_000,
	POLL: 6_000,
	REVEAL: 4_000,
	COMPARISON: 5_000,
	REFLECTION: 30_000,
	MOTION_GRAPHIC: 5_000,
} as const;

/** Card dwell bounds: a card never asks for less than MIN or more than MAX. */
export const TOPIC_CARD_DWELL_MIN_MS = 3_000;
export const TOPIC_CARD_DWELL_MAX_MS = 600_000;
/** Dwell for a card whose blocks are not known (pre-assembly shapes). */
export const FALLBACK_TOPIC_CARD_DWELL_MS = 5_000;

export function countWords(text: string): number {
	const normalized = text
		.replace(/<[^>]*>/g, " ")
		.replace(/\s+/g, " ")
		.trim();
	if (!normalized) {
		return 0;
	}
	return normalized.split(" ").length;
}

/** Unclamped time to get through `text` at `wordsPerMinute`; 0 for empty text. */
export function estimateTextMs(
	text: string,
	wordsPerMinute: number = TOPIC_READING_WPM,
): number {
	const words = countWords(text);
	if (words === 0 || wordsPerMinute <= 0) {
		return 0;
	}
	return Math.round((words / wordsPerMinute) * 60_000);
}

/** Narration script target: speech-rate estimate clamped to [minMs, maxMs]. */
export function estimateNarrationTargetMs(
	text: string,
	options?: { wordsPerMinute?: number; minMs?: number; maxMs?: number },
): number {
	const minMs = options?.minMs ?? NARRATION_CARD_MIN_MS;
	const maxMs = options?.maxMs ?? NARRATION_CARD_MAX_MS;
	const raw = estimateTextMs(
		text,
		options?.wordsPerMinute ?? NARRATION_SPEECH_WPM,
	);
	return Math.min(maxMs, Math.max(minMs, raw));
}

/** Browser speech planning length: the measured utterance when known, else the slow-rate hint. */
export function estimateFallbackTtsPlanningMs(
	text: string,
	measuredMs?: number | null,
): number {
	if (typeof measuredMs === "number" && measuredMs > 0) {
		return measuredMs;
	}
	return estimateNarrationTargetMs(text, {
		wordsPerMinute: FALLBACK_TTS_PLANNING_SPEECH_WPM,
		minMs: FALLBACK_TTS_PLANNING_MIN_MS,
	});
}

/** Cached `durationMinutes`: null when nothing to consume, else whole minutes rounded up (≥ 1). */
export function msToDurationMinutes(totalMs: number): number | null {
	if (totalMs <= 0) {
		return null;
	}
	return Math.max(1, Math.ceil(totalMs / 60_000));
}
